import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ComercialLayout } from '../../components/comercial/ComercialLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from '../../components/ui/sonner';
import axios from 'axios';
import {
  Plus,
  UserPlus,
  Building2,
  Home,
  Phone,
  Calendar,
  ChevronRight,
  MoreVertical,
  User,
  Beaker,
  CheckCircle2,
  Send,
  X,
  FileText,
  CreditCard,
  Shield,
  Wrench,
  Clock,
  PhoneCall,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// CRM stages configuration - New terminology
const CRM_STAGES = [
  { 
    key: 'contacto_nuevo', 
    label: 'Contacto Nuevo', 
    description: 'Oportunidad captada',
    color: 'bg-sky-500/20 border-sky-500/30 text-sky-400',
    headerColor: 'bg-sky-500/10 border-sky-500/30',
    dotColor: 'bg-sky-500'
  },
  { 
    key: 'interesado', 
    label: 'Interesado', 
    description: 'Interés confirmado',
    color: 'bg-blue-500/20 border-blue-500/30 text-blue-400',
    headerColor: 'bg-blue-500/10 border-blue-500/30',
    dotColor: 'bg-blue-500'
  },
  { 
    key: 'pendiente_instalacion_prueba', 
    label: 'Pdte. Instalación Prueba', 
    description: 'Acepta prueba gratuita',
    color: 'bg-amber-500/20 border-amber-500/30 text-amber-400',
    headerColor: 'bg-amber-500/10 border-amber-500/30',
    dotColor: 'bg-amber-500'
  },
  { 
    key: 'prueba_activa', 
    label: 'Prueba Activa', 
    description: 'Prueba en curso',
    color: 'bg-purple-500/20 border-purple-500/30 text-purple-400',
    headerColor: 'bg-purple-500/10 border-purple-500/30',
    dotColor: 'bg-purple-500'
  },
  { 
    key: 'pendiente_contrato', 
    label: 'Pendiente de Contrato', 
    description: 'Completar checklist',
    color: 'bg-orange-500/20 border-orange-500/30 text-orange-400',
    headerColor: 'bg-orange-500/10 border-orange-500/30',
    dotColor: 'bg-orange-500'
  },
  { 
    key: 'cliente', 
    label: 'Cliente', 
    description: 'Contrato activo',
    color: 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400',
    headerColor: 'bg-emerald-500/10 border-emerald-500/30',
    dotColor: 'bg-emerald-500'
  },
  { 
    key: 'pendiente_retirada', 
    label: 'Pdte. Retirada', 
    description: 'No continúa',
    color: 'bg-red-500/20 border-red-500/30 text-red-400',
    headerColor: 'bg-red-500/10 border-red-500/30',
    dotColor: 'bg-red-500'
  },
  { 
    key: 'perdido', 
    label: 'Perdido', 
    description: 'Oportunidad descartada',
    color: 'bg-zinc-500/20 border-zinc-500/30 text-zinc-400',
    headerColor: 'bg-zinc-500/10 border-zinc-500/30',
    dotColor: 'bg-zinc-500'
  },
];

const getStageConfig = (key) => CRM_STAGES.find(s => s.key === key) || CRM_STAGES[0];

// Progress Bar
function ProgressBar({ progress, size = 'sm' }) {
  const height = size === 'sm' ? 'h-1' : 'h-2';
  let color = 'bg-zinc-600';
  if (progress >= 100) color = 'bg-emerald-500';
  else if (progress >= 75) color = 'bg-orange-500';
  else if (progress >= 50) color = 'bg-purple-500';
  else if (progress >= 25) color = 'bg-blue-500';
  else if (progress > 0) color = 'bg-sky-500';
  
  return (
    <div className={`w-full ${height} bg-white/5 rounded-full overflow-hidden`}>
      <div className={`${height} ${color} transition-all duration-500`} style={{ width: `${progress}%` }} />
    </div>
  );
}

// Tarifas oficiales Manklar (cliente-side)
const TARIFAS_HOGAR = {
  KAJAAL: { precio: 29.90, nombre: "Kajaal - Hogar" },
  TIKAL: { precio: 34.90, nombre: "Tikal - Hogar" },
  TIKAL_S: { precio: 34.90, nombre: "Tikal S - Hogar" },
  CHAAK: { precio: 44.90, nombre: "Chaak - Hogar" },
  MAASKAB: { precio: null, nombre: "Máaskab - Hogar", pendiente: true },
};

// Checklist Modal
function ChecklistModal({ trial, onClose, onUpdate, tarifas, tecnicos }) {
  const [checklist, setChecklist] = useState(trial.checklist || {});
  const [saving, setSaving] = useState(false);
  const [sendingToTech, setSendingToTech] = useState(false);
  const [tarifaManual, setTarifaManual] = useState(false);

  const handleFieldChange = (field, value) => {
    setChecklist(prev => ({ ...prev, [field]: value }));
  };

  // Autocompletar tarifa cuando cambia el equipo
  const handleEquipoChange = (equipoId) => {
    handleFieldChange('equipo_definitivo', equipoId);
    
    // Autocompletar tarifa si no es manual
    if (!tarifaManual && equipoId && trial.tipo_cliente === 'hogar') {
      const tarifaInfo = TARIFAS_HOGAR[equipoId];
      if (tarifaInfo && tarifaInfo.precio) {
        setChecklist(prev => ({
          ...prev,
          equipo_definitivo: equipoId,
          tarifa_nombre: tarifaInfo.nombre,
          tarifa_precio: tarifaInfo.precio,
          tarifa_id: `hogar_${equipoId.toLowerCase()}`
        }));
      }
    }
  };

  const handleTarifaPrecioChange = (precio) => {
    setTarifaManual(true);
    handleFieldChange('tarifa_precio', parseFloat(precio) || null);
  };

  const handleTecnicoChange = (tecnicoId) => {
    const tecnico = tecnicos.find(t => t.id === tecnicoId);
    if (tecnico) {
      setChecklist(prev => ({
        ...prev,
        tecnico_asignado_id: tecnico.id,
        tecnico_asignado_nombre: tecnico.name
      }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/comercial/trials/${trial.id}`, { checklist }, { withCredentials: true });
      toast.success('Checklist guardado');
      onUpdate();
    } catch (err) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleSendToTechnical = async () => {
    setSendingToTech(true);
    try {
      await axios.put(`${API_URL}/api/comercial/trials/${trial.id}`, { checklist }, { withCredentials: true });
      const response = await axios.post(`${API_URL}/api/comercial/trials/${trial.id}/enviar-tecnico`, {}, { withCredentials: true });
      toast.success(`Enviado al Portal Técnico. Orden: ${response.data.order_number}`);
      onClose();
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar');
    } finally {
      setSendingToTech(false);
    }
  };

  // Mismo checklist que CHECKLIST_CONTRATO del backend: la tarifa cuenta por su precio (manual o autocompletado)
  const requiredFields = ['acuerdo_manklar', 'documentos_firmados', 'fianza_cobrada', 'suscripcion_creada', 'equipo_definitivo', 'tarifa_precio', 'metodo_pago', 'fecha_inicio_suscripcion', 'tecnico_asignado_id'];
  const completed = requiredFields.filter(f => checklist[f]).length;
  const completion = Math.round((completed / requiredFields.length) * 100);
  const isComplete = completed === requiredFields.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-[#0a0a0a] border border-white/10">
        <div className="sticky top-0 z-10 p-4 bg-[#0a0a0a] border-b border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-white">Checklist de Contrato</h3>
              <p className="text-xs text-zinc-500">{trial.client_code} · {trial.empresa || trial.nombre_contacto}</p>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10">
              <X className="w-5 h-5 text-zinc-400" />
            </button>
          </div>
          <div className="mt-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-zinc-500">Progreso</span>
              <span className={`text-xs font-medium ${isComplete ? 'text-emerald-400' : 'text-orange-400'}`}>{completion}%</span>
            </div>
            <ProgressBar progress={completion} size="md" />
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Documentación */}
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-zinc-500" />Documentación
            </h4>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={checklist.acuerdo_manklar || false} onChange={(e) => handleFieldChange('acuerdo_manklar', e.target.checked)} className="w-4 h-4 rounded border-white/20 bg-white/5 text-emerald-500" />
                <span className="text-sm text-zinc-300">Acuerdo Manklar</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={checklist.documentos_firmados || false} onChange={(e) => handleFieldChange('documentos_firmados', e.target.checked)} className="w-4 h-4 rounded border-white/20 bg-white/5 text-emerald-500" />
                <span className="text-sm text-zinc-300">Documento firmado</span>
              </label>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Estado del envío</label>
                <select value={checklist.estado_envio_docs || ''} onChange={(e) => handleFieldChange('estado_envio_docs', e.target.value)} className="w-full h-10 px-3 rounded-md bg-white/[0.03] border border-white/10 text-white text-sm">
                  <option value="" className="bg-zinc-900">No enviado</option>
                  <option value="enviado_whatsapp" className="bg-zinc-900">Enviado por WhatsApp</option>
                  <option value="enviado_email" className="bg-zinc-900">Enviado por Email</option>
                  <option value="pendiente_firma" className="bg-zinc-900">Pendiente de firma</option>
                  <option value="firmado" className="bg-zinc-900">Firmado</option>
                </select>
              </div>
              
              {/* Los documentos oficiales (PDF, firma, envío) viven en la ficha del cliente */}
              <div className="pt-2 border-t border-white/[0.06]">
                <Link
                  to={`/comercial/cliente/${trial.id}?tab=documentos`}
                  className="flex items-center justify-center gap-2 w-full h-9 rounded-md border border-sky-500/30 text-sky-400 hover:bg-sky-500/10 text-sm"
                  data-testid="checklist-ir-documentos"
                >
                  <FileText className="w-3.5 h-3.5" />Generar y enviar documentos
                </Link>
                <p className="text-[10px] text-zinc-600 mt-2">Acuerdo, SEPA y demás plantillas oficiales, con firma y envío por WhatsApp o email.</p>
              </div>
            </div>
          </div>

          {/* Pagos */}
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-zinc-500" />Pagos y Fianza
            </h4>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={checklist.fianza_cobrada || false} onChange={(e) => handleFieldChange('fianza_cobrada', e.target.checked)} className="w-4 h-4 rounded border-white/20 bg-white/5 text-emerald-500" />
                <span className="text-sm text-zinc-300">Fianza retornable cobrada</span>
              </label>
              {checklist.fianza_cobrada && (
                <div className="ml-7">
                  <label className="block text-xs text-zinc-500 mb-1">Importe de la fianza (€)</label>
                  <Input type="number" step="0.01" placeholder="Ej: 50.00" value={checklist.fianza_importe || ''} onChange={(e) => handleFieldChange('fianza_importe', parseFloat(e.target.value))} className="bg-white/[0.03] border-white/10 text-white w-32" />
                </div>
              )}
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Método de pago</label>
                <select value={checklist.metodo_pago || ''} onChange={(e) => handleFieldChange('metodo_pago', e.target.value)} className="w-full h-10 px-3 rounded-md bg-white/[0.03] border border-white/10 text-white text-sm">
                  <option value="" className="bg-zinc-900">Seleccionar...</option>
                  <option value="sepa" className="bg-zinc-900">Domiciliación SEPA</option>
                  <option value="tarjeta" className="bg-zinc-900">Tarjeta</option>
                </select>
              </div>
            </div>
          </div>

          {/* Equipo y Tarifa */}
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4 text-zinc-500" />Equipo y Tarifa
            </h4>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Equipo definitivo</label>
                <select 
                  value={checklist.equipo_definitivo || ''} 
                  onChange={(e) => handleEquipoChange(e.target.value)} 
                  className="w-full h-10 px-3 rounded-md bg-white/[0.03] border border-white/10 text-white text-sm"
                >
                  <option value="" className="bg-zinc-900">Seleccionar...</option>
                  <option value="KAJAAL" className="bg-zinc-900">Kajaal</option>
                  <option value="TIKAL" className="bg-zinc-900">Tikal (3.5L)</option>
                  <option value="TIKAL_S" className="bg-zinc-900">Tikal S (1.7L)</option>
                  <option value="CHAAK" className="bg-zinc-900">Chaak (8L)</option>
                  <option value="MAASKAB" className="bg-zinc-900">Máaskab</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">
                  Tarifa mensual {trial.tipo_cliente === 'hogar' ? '(IVA incluido)' : '(+IVA)'}
                  <span className="text-zinc-600 ml-2">• Editable</span>
                </label>
                <div className="flex items-center gap-2">
                  <Input 
                    type="number" 
                    step="0.01"
                    placeholder={trial.tipo_cliente === 'empresa' ? 'Pendiente Odoo' : 'Ej: 34.90'} 
                    value={checklist.tarifa_precio || ''} 
                    onChange={(e) => handleTarifaPrecioChange(e.target.value)} 
                    className="bg-white/[0.03] border-white/10 text-white flex-1" 
                  />
                  <span className="text-zinc-400 text-sm">€/mes</span>
                </div>
                {checklist.equipo_definitivo && trial.tipo_cliente === 'hogar' && TARIFAS_HOGAR[checklist.equipo_definitivo]?.precio && !tarifaManual && (
                  <p className="text-xs text-emerald-400 mt-1">Tarifa sugerida aplicada automáticamente</p>
                )}
                {tarifaManual && (
                  <p className="text-xs text-amber-400 mt-1">Tarifa personalizada (promoción/acuerdo comercial)</p>
                )}
              </div>
            </div>
          </div>

          {/* Programación */}
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-zinc-500" />Programación
            </h4>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Fecha inicio suscripción</label>
                <Input type="date" value={checklist.fecha_inicio_suscripcion || ''} onChange={(e) => handleFieldChange('fecha_inicio_suscripcion', e.target.value)} className="bg-white/[0.03] border-white/10 text-white" />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Técnico asignado</label>
                <select value={checklist.tecnico_asignado_id || ''} onChange={(e) => handleTecnicoChange(e.target.value)} className="w-full h-10 px-3 rounded-md bg-white/[0.03] border border-white/10 text-white text-sm">
                  <option value="" className="bg-zinc-900">Seleccionar...</option>
                  {tecnicos.map(t => (
                    <option key={t.id} value={t.id} className="bg-zinc-900">{t.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Botón Agendar Instalación Definitiva */}
              <div className="pt-3 border-t border-white/[0.06]">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toast.info('Calendario Inteligente - Próximamente')}
                  className="w-full border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                  data-testid="agendar-instalacion-btn"
                >
                  <Calendar className="w-4 h-4 mr-2" />Agendar Instalación Definitiva
                </Button>
                <p className="text-[10px] text-zinc-600 mt-2 text-center">Próximamente: Calendario Inteligente con sincronización Odoo</p>
              </div>
            </div>
          </div>

          {/* Notas */}
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
              <Wrench className="w-4 h-4 text-zinc-500" />Notas para el técnico
            </h4>
            <textarea value={checklist.notas_comercial || ''} onChange={(e) => handleFieldChange('notas_comercial', e.target.value)} placeholder="Instrucciones especiales..." rows={3} className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/10 text-white placeholder:text-zinc-600 text-sm resize-none" />
          </div>
        </div>

        <div className="sticky bottom-0 p-4 bg-[#0a0a0a] border-t border-white/10 flex items-center justify-between gap-3">
          <Button variant="outline" onClick={handleSave} disabled={saving} className="border-white/20 text-zinc-300">
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
          <Button onClick={handleSendToTechnical} disabled={!isComplete || sendingToTech || trial.enviado_portal_tecnico} className={`${isComplete && !trial.enviado_portal_tecnico ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-zinc-700'} text-white`}>
            {trial.enviado_portal_tecnico ? (<><CheckCircle2 className="w-4 h-4 mr-2" />Ya enviado</>) : sendingToTech ? 'Enviando...' : (<><Send className="w-4 h-4 mr-2" />Enviar al Técnico</>)}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Client Card
function ClientCard({ trial, onMove, stageConfig, onOpenChecklist, onOpenFicha }) {
  const [showMenu, setShowMenu] = useState(false);
  
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  const getNextStages = () => {
    const currentIndex = CRM_STAGES.findIndex(s => s.key === trial.crm_stage);
    return CRM_STAGES.filter((_, idx) => idx !== currentIndex);
  };

  const isPendienteContrato = trial.crm_stage === 'pendiente_contrato';
  const isHogar = trial.tipo_cliente === 'hogar';
  const displayName = isHogar ? trial.nombre_contacto : (trial.empresa || trial.nombre_contacto);

  return (
    <div 
      className="p-4 rounded-xl bg-[#0a0a0a] border border-white/[0.08] hover:border-white/15 transition-all cursor-pointer group"
      onClick={() => isPendienteContrato ? onOpenChecklist(trial) : onOpenFicha(trial)}
    >
      <div className="mb-3">
        <ProgressBar progress={trial.progreso || stageConfig.progress} />
      </div>
      
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {trial.client_code && (
              <span className="px-1.5 py-0.5 rounded text-[10px] bg-white/10 text-zinc-400 font-mono">{trial.client_code}</span>
            )}
            {isHogar ? (
              <Home className="w-3 h-3 text-sky-400" />
            ) : (
              <Building2 className="w-3 h-3 text-amber-400" />
            )}
            {trial.enviado_portal_tecnico && (
              <span className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/20 text-emerald-400">En técnico</span>
            )}
          </div>
          <h4 className="text-sm font-medium text-white line-clamp-1">{displayName}</h4>
        </div>
        <div className="relative">
          <button onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }} className="p-1 rounded hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity">
            <MoreVertical className="w-4 h-4 text-zinc-500" />
          </button>
          
          {showMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setShowMenu(false); }} />
              <div className="absolute right-0 top-6 z-20 w-48 py-2 rounded-xl bg-zinc-900 border border-white/10 shadow-xl">
                <p className="px-3 py-1 text-[10px] text-zinc-500 uppercase">Mover a</p>
                {getNextStages().map(stage => (
                  <button key={stage.key} onClick={(e) => { e.stopPropagation(); onMove(trial.id, stage.key); setShowMenu(false); }} className="w-full px-3 py-2 text-left text-sm text-zinc-300 hover:bg-white/5">
                    {stage.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <User className="w-3 h-3" />
          <span className="line-clamp-1">{trial.nombre_contacto}</span>
        </div>
        
        {trial.telefono && (
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <Phone className="w-3 h-3" />
            <span>{trial.telefono}</span>
          </div>
        )}

        {trial.crm_stage === 'prueba_activa' && trial.dias_en_prueba != null && (
          <div className="flex items-center gap-2 text-xs text-purple-400">
            <Beaker className="w-3 h-3" />
            <span>Día {trial.dias_en_prueba + 1} de prueba</span>
          </div>
        )}

        {trial.fecha_prevista && ['contacto_nuevo', 'interesado', 'pendiente_instalacion_prueba'].includes(trial.crm_stage) && (
          <div className="flex items-center gap-2 text-xs text-amber-400">
            <Calendar className="w-3 h-3" />
            <span>Visita: {formatDate(trial.fecha_prevista)}</span>
          </div>
        )}

        {isPendienteContrato && (
          <div className="mt-2 p-2 rounded-lg bg-orange-500/10 border border-orange-500/20">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-orange-300">Checklist</span>
              <span className="text-[10px] text-orange-400">{trial.checklist_progreso || 0}%</span>
            </div>
            <ProgressBar progress={trial.checklist_progreso || 0} />
          </div>
        )}

        {trial.origen_cliente && trial.origen_cliente !== 'otros' && (
          <span className="inline-flex px-2 py-1 rounded bg-white/5 text-[10px] text-zinc-400 capitalize">
            {trial.origen_cliente.replace('_', ' ')}
          </span>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-white/[0.04] flex items-center justify-between">
        <span className="text-[10px] text-zinc-600">{formatDate(trial.created_at)}</span>
        {trial.comercial_name && <span className="text-[10px] text-zinc-500">{trial.comercial_name}</span>}
      </div>
    </div>
  );
}

// CRM Column
function CRMColumn({ stage, trials, onMoveTrial, onOpenChecklist, onOpenFicha }) {
  return (
    <div className="flex-shrink-0 w-[280px] lg:w-[300px]">
      <div className={`p-3 rounded-t-xl border-b-0 ${stage.headerColor} border`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${stage.dotColor}`} />
            <div>
              <h3 className={`text-sm font-medium ${stage.color.split(' ').pop()}`}>{stage.label}</h3>
              <p className="text-[10px] text-zinc-500">{stage.description}</p>
            </div>
          </div>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${stage.color}`}>{trials.length}</span>
        </div>
      </div>

      <div className="p-2 rounded-b-xl bg-white/[0.01] border border-t-0 border-white/[0.06] min-h-[400px] max-h-[calc(100vh-300px)] overflow-y-auto space-y-2">
        {trials.map(trial => (
          <ClientCard key={trial.id} trial={trial} stageConfig={stage} onMove={onMoveTrial} onOpenChecklist={onOpenChecklist} onOpenFicha={onOpenFicha} />
        ))}
        {trials.length === 0 && (
          <div className="text-center py-8">
            <p className="text-xs text-zinc-600">Sin elementos</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CRM() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [crmData, setCrmData] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedTrial, setSelectedTrial] = useState(null);
  const [tarifas, setTarifas] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);

  const fetchCRM = useCallback(async () => {
    try {
      setLoadError(false);
      const response = await axios.get(`${API_URL}/api/comercial/crm`, { withCredentials: true });
      setCrmData(response.data);
    } catch (err) {
      console.error('Error fetching CRM:', err);
      setLoadError(true);
      toast.error('Error al cargar el CRM');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTarifasAndTecnicos = useCallback(async () => {
    try {
      const [tarifasRes, tecnicosRes] = await Promise.all([
        axios.get(`${API_URL}/api/comercial/tarifas`, { withCredentials: true }),
        axios.get(`${API_URL}/api/comercial/tecnicos`, { withCredentials: true })
      ]);
      setTarifas(tarifasRes.data);
      setTecnicos(tecnicosRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  }, []);

  useEffect(() => {
    fetchCRM();
    fetchTarifasAndTecnicos();
  }, [fetchCRM, fetchTarifasAndTecnicos]);

  const handleMoveTrial = async (trialId, newStage) => {
    try {
      await axios.put(`${API_URL}/api/comercial/trials/${trialId}`, { crm_stage: newStage }, { withCredentials: true });
      toast.success('Estado actualizado');
      fetchCRM();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleOpenFicha = (trial) => {
    navigate(`/comercial/cliente/${trial.id}`);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <img src="/manklar-logo-white.png" alt="Manklar" className="h-10 opacity-50" />
          <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <ComercialLayout title="CRM" subtitle="Error de carga" onLogout={handleLogout}>
        <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="crm-error-state">
          <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h3 className="text-lg font-light text-white font-['Outfit'] mb-2">No se pudo cargar el CRM</h3>
          <p className="text-sm text-zinc-500 max-w-md mb-6 font-['Manrope']">
            Ha ocurrido un error al obtener los datos. Comprueba tu conexión e inténtalo de nuevo.
          </p>
          <Button
            onClick={() => { setLoading(true); fetchCRM(); }}
            className="bg-white text-black hover:bg-zinc-200"
            data-testid="crm-retry-btn"
          >
            Reintentar
          </Button>
        </div>
      </ComercialLayout>
    );
  }

  // Calculate totals
  const activeStages = ['contacto_nuevo', 'interesado', 'pendiente_instalacion_prueba', 'prueba_activa', 'pendiente_contrato', 'cliente'];
  const totalActive = activeStages.reduce((acc, stage) => acc + (crmData[stage]?.count || 0), 0);

  return (
    <ComercialLayout title="CRM" subtitle={`${totalActive} oportunidades`} onLogout={handleLogout}
      headerActions={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/comercial/nuevo-contacto')} className="border-white/20 text-zinc-300 hover:text-white rounded-xl hidden sm:flex">
            <UserPlus className="w-4 h-4 mr-2" />Nuevo Contacto
          </Button>
          <Button onClick={() => navigate('/comercial/nueva-prueba')} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl">
            <Plus className="w-4 h-4 mr-2" /><span className="hidden sm:inline">Nueva Prueba</span><span className="sm:hidden">Prueba</span>
          </Button>
        </div>
      }
    >
      {/* Legend */}
      <div className="mb-6 flex flex-wrap gap-3">
        {CRM_STAGES.slice(0, 6).map(stage => (
          <div key={stage.key} className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${stage.dotColor}`} />
            <span className="text-xs text-zinc-500">{stage.label}</span>
          </div>
        ))}
      </div>

      {/* CRM Board */}
      <div className="overflow-x-auto pb-4 -mx-4 lg:-mx-8 px-4 lg:px-8">
        <div className="flex gap-4 min-w-max">
          {CRM_STAGES.map(stage => (
            <CRMColumn key={stage.key} stage={stage} trials={crmData[stage.key]?.trials || []} onMoveTrial={handleMoveTrial} onOpenChecklist={setSelectedTrial} onOpenFicha={handleOpenFicha} />
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="mt-8 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-6">
            <div>
              <p className="text-2xl font-light text-purple-400">{crmData['prueba_activa']?.count || 0}</p>
              <p className="text-xs text-zinc-500">Pruebas Activas</p>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div>
              <p className="text-2xl font-light text-orange-400">{crmData['pendiente_contrato']?.count || 0}</p>
              <p className="text-xs text-zinc-500">Pendientes Contrato</p>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div>
              <p className="text-2xl font-light text-emerald-400">{crmData['cliente']?.count || 0}</p>
              <p className="text-xs text-zinc-500">Clientes</p>
            </div>
          </div>
          <Button variant="outline" className="border-white/10 text-zinc-400 hover:text-white" onClick={() => navigate('/comercial')}>
            Panel Comercial<ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* Checklist Modal */}
      {selectedTrial && (
        <ChecklistModal trial={selectedTrial} onClose={() => setSelectedTrial(null)} onUpdate={() => { fetchCRM(); setSelectedTrial(null); }} tarifas={tarifas} tecnicos={tecnicos} />
      )}
    </ComercialLayout>
  );
}
